import { useEffect, useRef, useState } from 'react'
import {
  confirmEyemedCodeSetup,
  getEyemedSetupCode,
  openEyemedCodeSetup,
} from '../services/eyemedSetup'
import type { EyemedRowInput } from '../utils/eyemedRowStatus'

interface EyemedSetupModalProps {
  orgId: string
  clinicId: string
  clinicName: string
  /** The EyeMed username saved for the clinic, named in the steps. */
  username: string
  /** The login's state as its row shows it: a VitalRev pause takes no confirmation. */
  state: EyemedRowInput
  onClose: () => void
  /** The admin's confirmation was saved — the rows reload their states. */
  onConfirmed: () => void
  /** The login now reads as set up — the rows reload their states. */
  onSetUp: () => void
  /** Wait for EyeMed's confirmation code and show it. Off by default: EyeMed
   *  asks for no code when a user's email is changed, so the popup shows only
   *  the address and where to enter it. */
  showConfirmation?: boolean
}

/** How often the open popup asks for EyeMed's confirmation code. */
export const SETUP_POLL_MS = 5_000

type Opened =
  | { kind: 'loading' }
  | { kind: 'address'; address: string }
  | { kind: 'no_domain' }
  | { kind: 'failed'; message: string }

function errorMessage(err: unknown): string {
  return err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : ''
}

/**
 * The EyeMed code setup popup (openspec change eyemed-mfa-email-codes), opened
 * only from a clinic's 2FA button: the address the admin enters as the email
 * of the EyeMed user VitalRev logs in with, where to enter it, and the admin's
 * "Yes, I put it into EyeMed" in every state but a pause VitalRev is fixing,
 * which takes none; the cross closes it unanswered. With `showConfirmation`,
 * also EyeMed's confirmation code as soon as it reaches that address; it then
 * polls only while open. This is the one place in the app a code is ever shown.
 */
export default function EyemedSetupModal({
  orgId, clinicId, clinicName, username, state, onClose, onConfirmed, onSetUp, showConfirmation = false,
}: EyemedSetupModalProps) {
  const [opened, setOpened] = useState<Opened>({ kind: 'loading' })
  const [code, setCode] = useState<string | null>(null)
  const [setUp, setSetUp] = useState(false)
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const onSetUpRef = useRef(onSetUp)
  useEffect(() => { onSetUpRef.current = onSetUp }, [onSetUp])

  useEffect(() => {
    let cancelled = false
    openEyemedCodeSetup(orgId, clinicId)
      .then((res) => {
        if (cancelled) return
        setOpened('address' in res ? { kind: 'address', address: res.address } : { kind: 'no_domain' })
      })
      .catch((err: unknown) => {
        if (!cancelled) setOpened({ kind: 'failed', message: errorMessage(err) })
      })
    return () => { cancelled = true }
  }, [orgId, clinicId])

  // One poll at a time, every 5 s, until the login reads as set up or the
  // popup closes.
  useEffect(() => {
    if (!showConfirmation || opened.kind !== 'address' || setUp) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const tick = async () => {
      try {
        const res = await getEyemedSetupCode(orgId, clinicId)
        if (cancelled) return
        if (res.code) setCode(res.code)
        if (res.state === 'set_up') {
          setSetUp(true)
          onSetUpRef.current()
          return
        }
      } catch {
        // The next poll tries again.
      }
      if (!cancelled) timer = setTimeout(tick, SETUP_POLL_MS)
    }
    timer = setTimeout(tick, SETUP_POLL_MS)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [showConfirmation, opened, setUp, orgId, clinicId])

  async function confirm() {
    setConfirming(true)
    setConfirmError(null)
    try {
      await confirmEyemedCodeSetup(orgId, clinicId)
      onConfirmed()
    } catch (err: unknown) {
      setConfirmError(errorMessage(err) || "This couldn't be saved.")
      setConfirming(false)
    }
  }

  const takesConfirmation = opened.kind === 'address' && state !== 'paused_vitalrev'

  return (
    <div className="doctor-modal-overlay" onClick={onClose}>
      <div className="doctor-modal" role="dialog" aria-label="EyeMed code setup" onClick={e => e.stopPropagation()}>
        <div className="doctor-modal__header">
          <div>
            <h3 className="doctor-modal__title">EyeMed code setup</h3>
            <p className="integrations-modal__clinic">{clinicName}</p>
          </div>
          <button className="doctor-modal__close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="add-doctor-form">
          {opened.kind === 'loading' && <p className="integrations-modal__desc">Getting this login&rsquo;s address…</p>}
          {opened.kind === 'no_domain' && (
            <p className="clinic-save-error" data-testid="eyemed-setup-no-domain">
              There&rsquo;s no address for this login yet. Contact VitalRev to finish the setup.
            </p>
          )}
          {opened.kind === 'failed' && (
            <p className="clinic-save-error" data-testid="eyemed-setup-failed">
              The setup couldn&rsquo;t be opened.{opened.message ? ` ${opened.message}` : ''}
            </p>
          )}
          {opened.kind === 'address' && (
            <>
              <label className="form-field__label" htmlFor="eyemed-setup-address">Email address</label>
              <div className="eyemed-setup__address">
                <input
                  id="eyemed-setup-address"
                  className="form-field__input"
                  data-testid="eyemed-setup-address"
                  value={opened.address}
                  readOnly
                />
                <button
                  className="check-btn"
                  data-testid="eyemed-setup-copy"
                  onClick={() => {
                    navigator.clipboard.writeText(opened.address).then(() => {
                      setCopied(true)
                      setTimeout(() => setCopied(false), 2000)
                    }).catch(() => {})
                  }}
                >
                  {copied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              <p className="eyemed-setup__heading">Add it in EyeMed</p>
              <ol className="eyemed-setup__steps">
                <li>Go to <strong>Manage My Profile</strong> → <strong>Manage authentication Info</strong>.</li>
                <li>
                  Paste the address as the email for user{' '}
                  <strong data-testid="eyemed-setup-username">{username}</strong>.
                </li>
              </ol>
              {showConfirmation && (
                <>
                  <p className="integrations-modal__desc">EyeMed then sends a code to this address to confirm it.</p>
                  <p className="integrations-modal__desc">Keep this window open until EyeMed&rsquo;s code shows here.</p>
                  {code ? (
                    <>
                      <p className="integrations-modal__desc">EyeMed&rsquo;s code — type it into EyeMed:</p>
                      <p className="eyemed-setup__value" data-testid="eyemed-setup-code">{code}</p>
                    </>
                  ) : (
                    !setUp && <p className="integrations-modal__desc" data-testid="eyemed-setup-waiting">Waiting for EyeMed&rsquo;s code…</p>
                  )}
                  {setUp && <p className="eyemed-setup__done" data-testid="eyemed-setup-done">This login is set up.</p>}
                </>
              )}
            </>
          )}
          {confirmError && <p className="clinic-save-error" data-testid="eyemed-setup-confirm-error">{confirmError}</p>}
          <div className="add-doctor-form__actions eyemed-setup__actions">
            <span />
            <div className="add-doctor-form__actions-right">
              {takesConfirmation ? (
                <button
                  className="check-btn add-doctor-form__submit"
                  data-testid="eyemed-setup-confirm"
                  disabled={confirming}
                  onClick={() => { void confirm() }}
                >
                  Yes, I put it into EyeMed
                </button>
              ) : (
                <button className="add-doctor-form__cancel" onClick={onClose}>Close</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
